import { genericActions } from "../../Everou/_actions/generic.actions";
import { PMSIntegrationsCollection } from "../_stores/PMSIntegrationsCollection";
import { PMSIntegrationsService } from "../_services/PMSIntegrationsService";
import { IntegrationMapper } from "../../Everou/_mappersTS/IntegrationMapper";
import { Integration } from "../../Everou/_modelsTS/Integration";
import { PMSAccountDraftStore } from "../_stores/PMSAccountDraftStore";
import { PMSKrossBookingService } from "../_services/PMSKrossBookingService";
import { PMSKrossBookingStore } from "../_stores/PMSKrossBookingStore";
import { KrossBookingMapper } from "../../Everou/_mappersTS/KrossBookingMapper";
import { PMSHostifyService } from "../_services/PMSHostifyService";
import { PMSHostifyStore } from "../_stores/PMSHostifyStore";
import { PMSSmoobuStore } from "../_stores/PMSSmoobuStore";
import { PMSSmoobuService } from "../_services/PMSSmoobuService";
import { PMSOctorateService } from "../_services/PMSOctorateService";
import { PMS_INTEGRATIONS_ACTION_TYPES } from "../_actionTypes/PMS_INTEGRATIONS_ACTION_TYPES";
import { PMSOctorateStore } from "../_stores/PMSOctorateStore";
import { PMSAvaibookStore } from "../_stores/PMSAvaibookStore";
import { PMSAvaibookService } from "../_services/PMSAvaibookService";
import { PMSLodgifyStore } from "../_stores/PMSLodgifyStore";
import { PMSLodgifyService } from "../_services/PMSLodgifyService";
import { PMSGuestyService } from "../_services/PMSGuestyService";
import { PMSGuestyStore } from "../_stores/PMSGuestyStore";
import { PMSAvantioService } from "../_services/PMSAvantioService";
import { PMSAvantioStore } from "../_stores/PMSAvantioStore";
import {
    ensureOrganizationsLoaded,
    resolveActiveOrganization,
    resolveLocationOrganizationUid,
} from "../_helpers/activeOrganization";

export const PMSIntegrationsActions = {
    listIntegrations,
    listIntegrationTemplatesForLocation,
    listIntegrationsRequest: PMSIntegrationsCollection.actionListItemsRequest,
    listIntegrationsSuccess: PMSIntegrationsCollection.actionListItemsSuccess,

    createIntegration,
    renameIntegration,
    deleteIntegration,
    syncIntegration,

    listKrossBookingProperties,
    listKrossBookingRooms,

    listHostifyListings,
    listHostifyListingsSuccess: PMSHostifyStore.actionListItemsSuccess,

    listSmoobuProperties,

    listOctorateAccommodations,
    octorateToken,
    getOctorateUserInfo,

    listAvaibookAccommodations,

    listLodgifyProperties,

    listGuestyListings,

    listAvantioAccommodations,
};

// Monotonic tag for the integrations listing. The organization check below
// only catches responses landing in a *different* scope: switching A -> B -> A
// leaves two requests for A in flight, and if the first (slowest) lands last
// its uid still matches, so it replaces the catalog with an older snapshot —
// visibly wrong as soon as an integration was created or deleted in between.
// Only the newest request may write the store. Same discard-stale-by-tag
// pattern as the available locations store in Organizations.
let listIntegrationsRequestId = 0;

function listIntegrations() {
    return async (dispatch, getState) => {
        return await dispatch(genericActions.genericAsyncAction(asyncAction));

        async function asyncAction() {
            // Organization-centric: members operate on their organization's
            // integration templates; the personal ones are the fallback for
            // users without an organization (BAC-215).
            if (!(await ensureOrganizationsLoaded(dispatch, getState))) {
                // Scope unknown: listing the personal templates instead
                // would show the wrong data to organization members.
                return;
            }
            const organizationUid = resolveActiveOrganization(getState())?.uid;
            const requestId = ++listIntegrationsRequestId;
            dispatch(PMSIntegrationsActions.listIntegrationsRequest());
            const localIntegrations = IntegrationMapper.allServerToLocal(
                await PMSIntegrationsService.list(organizationUid),
            );
            // The response only makes sense for the scope it was asked for:
            // if the active organization changed while it was in flight, a
            // late landing would show the previous scope's templates as the
            // new one's (and invite deleting the wrong integration). Discard
            // it — the scope switch already triggered its own refetch. Same
            // discard-by-uid pattern as the customization template store.
            // A newer request for the *same* scope supersedes this one too.
            if (
                resolveActiveOrganization(getState())?.uid !==
                    organizationUid ||
                requestId !== listIntegrationsRequestId
            ) {
                // Deliberately not returning the discarded list: the awaiting
                // caller (octorateToken) would take it for the current scope's
                // catalog. It ignores the value, so undefined is both honest
                // and harmless.
                return;
            }
            dispatch(
                PMSIntegrationsActions.listIntegrationsSuccess(
                    localIntegrations,
                ),
            );

            return localIntegrations;
        }
    };
}

// Templates attachable to a location: the organization's plus the personal
// (legacy) ones, which the backend still accepts and which would otherwise
// vanish from the picker for anyone who had them before organizations.
// Deliberately NOT stored in the shared integrations collection: that one
// holds the Integrations module's scope, and writing a merged list into it
// made the module show personal templates as if they were the
// organization's until its own fetch resolved. Returns them instead, for the
// caller to keep in local state.
function listIntegrationTemplatesForLocation() {
    return async (dispatch, getState) => {
        // The caller renders loading/empty/error as distinct states, so let it
        // observe a real failure instead of turning it into `undefined`.
        return await dispatch(
            genericActions.genericAsyncAction(asyncAction, null, true),
        );

        async function asyncAction() {
            if (!(await ensureOrganizationsLoaded(dispatch, getState))) {
                throw new Error("ORGANIZATIONS_NOT_LOADED");
            }
            const organizationUid = resolveScope();

            // ServiceHelper expires the previous in-flight request by endpoint,
            // without considering its query string. These two listings hit the
            // same endpoint, so running them in parallel always makes the
            // personal request invalidate the organization's request. Sequence
            // them to keep both scopes.
            const organizationTemplates = await PMSIntegrationsService.list(
                organizationUid,
            );
            let personalTemplates = [];
            if (organizationUid) {
                try {
                    personalTemplates = await PMSIntegrationsService.list();
                } catch (_) {
                    // Personal templates are a legacy, optional fallback. A
                    // failure there must not discard the active organization's
                    // valid catalog.
                }
            }

            return [
                ...IntegrationMapper.allServerToLocal(organizationTemplates),
                ...IntegrationMapper.allServerToLocal(personalTemplates).map(
                    (template) => ({ ...template, isPersonal: true }),
                ),
            ];
        }

        // Whose templates apply here -- see resolveLocationOrganizationUid
        // (BAC-638, BAC-650).
        function resolveScope() {
            return resolveLocationOrganizationUid(getState());
        }
    };
}

// Rename an existing template without going through its save (BAC-520).
function renameIntegration(uid: string, name: string) {
    return async (dispatch, getState) => {
        return await dispatch(
            genericActions.genericAsyncAction(asyncAction, null, true),
        );

        async function asyncAction() {
            if (!(await ensureOrganizationsLoaded(dispatch, getState))) {
                // Renaming in the wrong scope answers "not found" and would
                // read as the template having vanished.
                throw new Error("ORGANIZATIONS_NOT_LOADED");
            }
            const organizationUid = resolveActiveOrganization(getState())?.uid;
            // Deliberately no listIntegrations(): re-listing rebuilds every
            // template row, and the forms reset their fields from the new
            // instance -- renaming would throw away an apikey the manager had
            // typed but not saved yet. The caller keeps the new name locally
            // and the store catches up on its next refresh.
            return await PMSIntegrationsService.rename(uid, name, organizationUid);
        }
    };
}

function createIntegration(localIntegration: Integration) {
    return async (dispatch, getState) => {
        return await dispatch(
            genericActions.genericAsyncAction(asyncAction, null, true),
        );

        async function asyncAction() {
            if (!(await ensureOrganizationsLoaded(dispatch, getState))) {
                // Creating in the wrong scope is worse than failing.
                throw new Error("ORGANIZATIONS_NOT_LOADED");
            }
            const organizationUid = resolveActiveOrganization(getState())?.uid;
            const serverIntegration =
                IntegrationMapper.localToServer(localIntegration);
            const integration = await PMSIntegrationsService.create(
                serverIntegration,
                organizationUid,
            );

            // Saved: the prefilled row it may have come from must not be
            // seeded again when the list refreshes. Only a NEW account of the
            // draft's company can be that row: updating another account
            // leaves the draft, half-typed or not, where it was.
            const draft = PMSAccountDraftStore.selectorGet(getState());
            if (!localIntegration.uid && draft?.company === localIntegration.company) {
                dispatch(PMSAccountDraftStore.actionSet(null));
            }
            dispatch(listIntegrations());

            return integration;
        }
    };
}

function syncIntegration(locUid: string, uid: string, runAsync = true) {
    return async (dispatch) => {
        // rethrow so the caller can react to specific error codes (699/682).
        return await dispatch(
            genericActions.genericAsyncAction(asyncAction, null, true),
        );

        async function asyncAction() {
            return await PMSIntegrationsService.sync(locUid, uid, runAsync);
        }
    };
}

function deleteIntegration(integrationUid: string) {
    return async (dispatch, getState) => {
        return await dispatch(genericActions.genericAsyncAction(asyncAction));

        async function asyncAction() {
            // Same scope resolution as createIntegration: deleting in the wrong
            // scope does not find the template (101 uid not found).
            if (!(await ensureOrganizationsLoaded(dispatch, getState))) {
                throw new Error("ORGANIZATIONS_NOT_LOADED");
            }
            const organizationUid = resolveActiveOrganization(getState())?.uid;
            await PMSIntegrationsService.deleteIntegration(
                integrationUid,
                organizationUid,
            );
            dispatch(
                PMSIntegrationsCollection.actionDeleteItem(integrationUid),
            );
        }
    };
}

function listKrossBookingProperties(integrationUid: string) {
    return async (dispatch) => {
        return await dispatch(genericActions.genericAsyncAction(asyncAction));

        async function asyncAction() {
            const serverKrossBookingProperties =
                await PMSKrossBookingService.listProperties(integrationUid);
            dispatch(
                PMSKrossBookingStore.actionListItemsSuccess(
                    serverKrossBookingProperties,
                ),
            );
        }
    };
}

function listKrossBookingRooms(uid: string, host_id: number) {
    return async (dispatch) => {
        return await dispatch(genericActions.genericAsyncAction(asyncAction));

        async function asyncAction() {
            const serverKrossBookingRooms =
                await PMSKrossBookingService.listRooms(uid, host_id);
            const rooms = KrossBookingMapper.serverListRoomToLocal(
                serverKrossBookingRooms,
            );
            dispatch(
                PMSKrossBookingStore.actionUpdateItemSuccess({
                    id_property: host_id,
                    rooms,
                }),
            );
        }
    };
}

function listHostifyListings(integrationUID: string, page = 0) {
    return async (dispatch) => {
        return await dispatch(genericActions.genericAsyncAction(asyncAction));

        async function asyncAction() {
            const { listings, next } = await PMSHostifyService.listListings(
                integrationUID,
                page,
            );
            dispatch(
                PMSIntegrationsActions.listHostifyListingsSuccess(
                    listings,
                    next,
                ),
            );
        }
    };
}

function listSmoobuProperties(integrationUID: string) {
    return async (dispatch) => {
        return await dispatch(genericActions.genericAsyncAction(asyncAction));

        async function asyncAction() {
            const listings = await PMSSmoobuService.listProperties(
                integrationUID,
            );
            dispatch(PMSSmoobuStore.actionListItemsSuccess(listings));
        }
    };
}

function octorateToken(code: any) {
    return async (dispatch) => {
        return await dispatch(
            genericActions.genericAsyncAction(asyncAction, null, true),
        );

        async function asyncAction() {
            await PMSOctorateService.token(code);
            return await dispatch(listIntegrations());
        }
    };
}

function listOctorateAccommodations(integrationUID: string) {
    return async (dispatch) => {
        return await dispatch(genericActions.genericAsyncAction(asyncAction));

        async function asyncAction() {
            const listings = await PMSOctorateService.listAccommodations(
                integrationUID,
            );
            dispatch(PMSOctorateStore.actionListItemsSuccess(listings));
        }
    };
}

function getOctorateUserInfo(integrationUID: string) {
    return async (dispatch) => {
        return await dispatch(genericActions.genericAsyncAction(asyncAction));

        async function asyncAction() {
            const userInfo = await PMSOctorateService.userInfo(integrationUID);
            dispatch(getOctorateUserInfoSuccess(userInfo));
        }

        function getOctorateUserInfoSuccess(userInfo) {
            return {
                type: PMS_INTEGRATIONS_ACTION_TYPES.GET_OCTORATE_USER_INFO_SUCCESS,
                userInfo,
            };
        }
    };
}

function listAvaibookAccommodations(integrationUID: string) {
    return async (dispatch) => {
        return await dispatch(genericActions.genericAsyncAction(asyncAction));

        async function asyncAction() {
            const listings = await PMSAvaibookService.listAccommodations(
                integrationUID,
            );
            dispatch(PMSAvaibookStore.actionListItemsSuccess(listings));
        }
    };
}

function listLodgifyProperties(integrationUID: string) {
    return async (dispatch) => {
        return await dispatch(genericActions.genericAsyncAction(asyncAction));

        async function asyncAction() {
            const properties = await PMSLodgifyService.listProperties(
                integrationUID,
            );
            dispatch(PMSLodgifyStore.actionListItemsSuccess(properties));
        }
    };
}

function listGuestyListings(integrationUID: string) {
    return async (dispatch) => {
        return await dispatch(genericActions.genericAsyncAction(asyncAction));

        async function asyncAction() {
            const listings = await PMSGuestyService.listListings(
                integrationUID,
            );
            dispatch(PMSGuestyStore.actionListItemsSuccess(listings));
        }
    };
}

function listAvantioAccommodations(integrationUID: string) {
    return async (dispatch) => {
        return await dispatch(genericActions.genericAsyncAction(asyncAction));

        async function asyncAction() {
            const accommodations = await PMSAvantioService.listAccommodations(
                integrationUID,
            );
            dispatch(PMSAvantioStore.actionListItemsSuccess(accommodations));
        }
    };
}
